import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, RotateCcw, Moon, Sparkles } from 'lucide-react';
import { colors, spacing, typography, shadows, transitions } from '../../styles/designSystem';
import { AI_CONFIG, AI_INITIAL_GREETING_EN, AI_INITIAL_GREETING_ES } from '../../config/ai-config';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const ChatbotWrapper = styled.div`
  position: fixed;
  bottom: ${spacing[6]};
  right: ${spacing[6]};
  z-index: 1000;
  
  @media (max-width: 768px) {
    bottom: ${spacing[3]};
    right: ${spacing[3]};
    left: auto;
  }
  
  @media (max-width: 480px) {
    bottom: ${spacing[2]};
    right: ${spacing[2]};
  }
  
  .chatbot-message__cursor {
    @keyframes blink {
      0%, 50% { opacity: 1; }
      51%, 100% { opacity: 0; }
    }
  }
`;

const ChatToggle = styled(motion.button)`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.sacred.night} 0%, ${colors.tiffany[500]} 50%, ${colors.tiffany[600]} 100%);
  border: 3px solid rgba(255, 255, 255, 0.3);
  color: ${colors.sacred.white};
  cursor: pointer;
  box-shadow: ${shadows.sacred};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 24px;
    margin-left: auto;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: rotate(45deg);
    animation: shimmer 3s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 30px rgba(20, 184, 166, 0.4);
  }
`;

const MobileHiddenToggle = styled(ChatToggle)`
  @media (max-width: 768px) {
    ${props => props.$isOpen && `
      opacity: 0;
      pointer-events: none;
      transform: scale(0.8);
      transition: all 0.3s ease;
    `}
  }
`;

const ChatWindow = styled(motion.div)`
  position: absolute;
  bottom: 85px;
  right: 0;
  width: 400px;
  height: 520px;
  max-height: calc(100vh - 120px);
  background: linear-gradient(135deg, ${colors.sacred.white} 0%, ${colors.sacred.whisper} 100%);
  border-radius: ${spacing[6]};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(20, 184, 166, 0.2);
  overflow: hidden;
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: 20px;
    left: ${spacing[3]};
    right: ${spacing[3]};
    top: 20px;
    width: auto;
    height: auto;
    max-height: none;
    border-radius: ${spacing[4]};
  }
  
  @media (max-width: 480px) {
    bottom: 15px;
    left: ${spacing[2]};
    right: ${spacing[2]};
    top: 15px;
    border-radius: ${spacing[3]};
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, ${colors.sacred.night} 0%, ${colors.tiffany[600]} 100%);
  color: ${colors.sacred.white};
  padding: ${spacing[4]} ${spacing[5]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  min-height: 72px;
  
  @media (max-width: 768px) {
    padding: ${spacing[3]} ${spacing[4]};
    min-height: 60px;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  flex: 1;
`;

const Avatar = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.sacred.white} 0%, ${colors.tiffany[100]} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  position: relative;
  
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    font-size: 20px;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    right: -2px;
    width: 12px;
    height: 12px;
    background: #00ff88;
    border-radius: 50%;
    border: 2px solid ${colors.sacred.white};
    
    @media (max-width: 768px) {
      width: 10px;
      height: 10px;
    }
  }
`;

const HeaderDetails = styled.div`
  flex: 1;
`;

const BotName = styled.h3`
  font-family: ${typography.fonts.display};
  font-size: ${typography.sizes.lg};
  margin: 0;
  color: ${colors.sacred.white};
  
  @media (max-width: 768px) {
    font-size: ${typography.sizes.md};
  }
`;

const BotSubtitle = styled.p`
  font-size: ${typography.sizes.sm};
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
  
  @media (max-width: 768px) {
    font-size: ${typography.sizes.xs};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${spacing[2]};
`;

const HeaderButton = styled(motion.button)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${colors.sacred.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing[4]};
  min-height: 0;
  
  @media (max-width: 768px) {
    padding: ${spacing[3]};
  }
  
  @media (max-width: 480px) {
    padding: ${spacing[2]} ${spacing[3]};
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colors.sacred.mist};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.tiffany[400]};
    border-radius: 3px;
  }
`;

const Message = styled(motion.div)`
  margin-bottom: ${spacing[4]};
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  
  @media (max-width: 768px) {
    gap: ${spacing[2]};
    margin-bottom: ${spacing[3]};
  }
  
  ${props => props.$isUser && `
    flex-direction: row-reverse;
  `}
`;

const MessageBubble = styled.div`
  max-width: 85%;
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${spacing[4]};
  
  @media (max-width: 768px) {
    max-width: 90%;
    padding: ${spacing[2]} ${spacing[3]};
    font-size: ${typography.sizes.sm};
  }
  
  @media (max-width: 480px) {
    max-width: 95%;
    padding: ${spacing[2]};
  }
  
  ${props => props.$isUser ? `
    background: linear-gradient(135deg, ${colors.tiffany[500]} 0%, ${colors.tiffany[600]} 100%);
    color: ${colors.sacred.white};
    border-bottom-right-radius: ${spacing[2]};
  ` : `
    background: ${colors.sacred.white};
    color: ${colors.sacred.night};
    border: 1px solid ${colors.sacred.mist};
    border-bottom-left-radius: ${spacing[2]};
    box-shadow: ${shadows.gentle};
  `}
`;

const MessageContent = styled.div`
  font-size: ${typography.sizes.sm};
  line-height: ${typography.lineHeights.relaxed};
  
  h1, h2, h3 { 
    margin: ${spacing[3]} 0 ${spacing[2]} 0;
    color: ${colors.sacred.night};
  }
  
  h2 { 
    font-size: ${typography.sizes.lg};
    color: ${colors.tiffany[700]};
  }
  
  ul, ol {
    margin: ${spacing[2]} 0;
    padding-left: ${spacing[4]};
  }
  
  li {
    margin-bottom: ${spacing[1]};
  }
  
  strong {
    color: ${colors.tiffany[700]};
  }
  
  code {
    background: ${colors.sacred.mist};
    padding: ${spacing[1]} ${spacing[2]};
    border-radius: ${spacing[1]};
    font-size: ${typography.sizes.xs};
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin: ${spacing[3]} 0;
  }
  
  th, td {
    padding: ${spacing[2]};
    border: 1px solid ${colors.sacred.mist};
    text-align: left;
  }
  
  th {
    background: ${colors.tiffany[50]};
    font-weight: ${typography.weights.semibold};
  }
`;

const MessageTime = styled.div`
  font-size: ${typography.sizes.xs};
  color: ${colors.sacred.stone};
  margin-top: ${spacing[1]};
  text-align: ${props => props.$isUser ? 'right' : 'left'};
`;

const TypingIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  background: ${colors.sacred.white};
  border-radius: ${spacing[4]};
  border-bottom-left-radius: ${spacing[2]};
  box-shadow: ${shadows.gentle};
  margin-bottom: ${spacing[4]};
  
  @media (max-width: 768px) {
    padding: ${spacing[2]} ${spacing[3]};
    margin-bottom: ${spacing[3]};
    gap: ${spacing[1]};
  }
`;

const TypingDot = styled(motion.div)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.tiffany[500]};
  
  @media (max-width: 768px) {
    width: 6px;
    height: 6px;
  }
`;

const InputContainer = styled.div`
  padding: ${spacing[4]};
  border-top: 1px solid ${colors.sacred.mist};
  background: ${colors.sacred.white};
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: ${spacing[3]};
  }
`;

const InputWrapper = styled.div`
  display: flex;
  gap: ${spacing[3]};
  align-items: flex-end;
`;

const MessageInput = styled.textarea`
  flex: 1;
  padding: ${spacing[3]};
  border: 1px solid ${colors.sacred.mist};
  border-radius: ${spacing[3]};
  font-size: ${typography.sizes.sm};
  font-family: ${typography.fonts.body};
  resize: none;
  min-height: 40px;
  max-height: 100px;
  
  @media (max-width: 768px) {
    padding: ${spacing[2]};
    font-size: ${typography.sizes.sm};
    min-height: 38px;
    max-height: 80px;
  }
  
  &:focus {
    outline: none;
    border-color: ${colors.tiffany[400]};
    box-shadow: 0 0 0 2px rgba(20, 184, 166, 0.1);
  }
  
  &::placeholder {
    color: ${colors.sacred.stone};
    font-style: italic;
  }
`;

const SendButton = styled(motion.button)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.tiffany[500]} 0%, ${colors.tiffany[600]} 100%);
  border: none;
  color: ${colors.sacred.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:not(:disabled):hover {
    transform: scale(1.1);
  }
`;

const PoweredBy = styled.div`
  text-align: center;
  font-size: ${typography.sizes.xs};
  color: ${colors.sacred.void};
  margin-top: ${spacing[2]};
  
  a {
    color: ${colors.tiffany[600]};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Luna = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // Initialize with Luna's greeting based on current language
  const getInitialGreeting = () => {
    return i18n.language === 'es' ? AI_INITIAL_GREETING_ES : AI_INITIAL_GREETING_EN;
  };

  const [messages, setMessages] = useState([
    {
      id: 1,
      text: getInitialGreeting(),
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Update Luna's greeting when language changes
  useEffect(() => {
    setMessages([
      {
        id: 1,
        text: getInitialGreeting(),
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
  }, [i18n.language]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Convert UI messages to API format
  const convertMessagesToApiFormat = (uiMessages) => {
    return uiMessages.map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'assistant',
      content: msg.text
    }));
  };

  const sendMessage = async () => {
    if (!inputText.trim() || isStreaming) return;

    console.log('🌙 Luna: Sending sacred message:', inputText);

    const userMessage = {
      id: Date.now(),
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputText('');
    setIsTyping(true);
    setIsStreaming(true);

    try {
      // Convert messages to API format and prepare the payload
      const apiMessages = convertMessagesToApiFormat(updatedMessages);
      const userApiMessage = { role: 'user', content: inputText };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appName: 'amaspa',
          messages: [
            AI_CONFIG.systemMessage,
            ...apiMessages.filter(msg => msg.role !== 'system'),
            userApiMessage
          ],
          stream: true,
          temperature: parseFloat(process.env.REACT_APP_CHATBOT_TEMPERATURE) || 0.7,
          provider: process.env.REACT_APP_CHATBOT_PROVIDER || 'azure-gpt'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      // Create bot message for streaming
      const botMessageId = Date.now() + 1;
      const botMessage = {
        id: botMessageId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true
      };
      
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);

      let accumulatedText = '';
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        const chunk = decoder.decode(value);
        buffer += chunk;
        
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || trimmedLine === 'data: [DONE]') continue;
          
          if (trimmedLine.startsWith('data: ')) {
            try {
              const jsonStr = trimmedLine.slice(6);
              const parsed = JSON.parse(jsonStr);
              const content = parsed.content;
              if (content) {
                accumulatedText += content;
                setMessages(prev => prev.map(msg => 
                  msg.id === botMessageId 
                    ? { ...msg, text: accumulatedText }
                    : msg
                ));
                // Small delay for smooth streaming effect
                await new Promise(resolve => setTimeout(resolve, 15));
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
      
      // Mark streaming as complete
      setIsStreaming(false);
      setMessages(prev => prev.map(msg => 
        msg.id === botMessageId 
          ? { ...msg, isStreaming: false }
          : msg
      ));
      
    } catch (error) {
      console.error('🌙 Luna: Sacred error:', error);
      setIsTyping(false);
      setIsStreaming(false);
      
      const errorMessage = {
        id: Date.now() + 1,
        text: t('luna.errorMessage'),
        sender: 'bot',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: 1,
        text: AI_CONFIG.initialMessage.content,
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const markdownComponents = {
    h2: ({node, ...props}) => <h2 style={{color: colors.tiffany[700], fontSize: typography.sizes.lg}} {...props} />,
    strong: ({node, ...props}) => <strong style={{color: colors.tiffany[700]}} {...props} />,
    em: ({node, ...props}) => <em style={{color: colors.sacred.shadow, fontStyle: 'italic'}} {...props} />
  };

  return (
    <ChatbotWrapper>
      <AnimatePresence>
        {isOpen && (
          <ChatWindow
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <ChatHeader>
              <HeaderInfo>
                <Avatar>
                  🌙
                </Avatar>
                <HeaderDetails>
                  <BotName>Luna</BotName>
                  <BotSubtitle>Divine Wellness Oracle</BotSubtitle>
                </HeaderDetails>
              </HeaderInfo>
              <HeaderActions>
                <HeaderButton
                  onClick={clearChat}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <RotateCcw size={14} />
                </HeaderButton>
                <HeaderButton
                  onClick={() => setIsOpen(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X size={14} />
                </HeaderButton>
              </HeaderActions>
            </ChatHeader>

            <MessagesContainer>
              {messages.map((message) => (
                <Message
                  key={message.id}
                  $isUser={message.sender === 'user'}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <MessageBubble $isUser={message.sender === 'user'}>
                    <MessageContent>
                      {message.sender === 'bot' ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={markdownComponents}
                        >
                          {message.text}
                        </ReactMarkdown>
                      ) : (
                        message.text
                      )}
                      {message.isStreaming && (
                        <span className="chatbot-message__cursor" style={{ 
                          color: colors.tiffany[500],
                          fontSize: typography.sizes.lg,
                          animation: 'blink 1s infinite',
                          marginLeft: spacing[1]
                        }}>|</span>
                      )}
                    </MessageContent>
                    <MessageTime $isUser={message.sender === 'user'}>
                      {formatTime(message.timestamp)}
                    </MessageTime>
                  </MessageBubble>
                </Message>
              ))}

              {isTyping && (
                <TypingIndicator
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <span style={{ fontSize: typography.sizes.sm, color: colors.sacred.shadow }}>
                    {t('luna.channeling')}
                  </span>
                  <TypingDot
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                  />
                  <TypingDot
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                  />
                  <TypingDot
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                  />
                </TypingIndicator>
              )}
              
              <div ref={messagesEndRef} />
            </MessagesContainer>

            <InputContainer>
              <InputWrapper>
                <MessageInput
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('luna.placeholder')}
                  disabled={isStreaming}
                />
                <SendButton
                  onClick={sendMessage}
                  disabled={!inputText.trim() || isStreaming}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Sparkles size={18} />
                </SendButton>
              </InputWrapper>
              <PoweredBy>
                {t('luna.poweredBy')} <a href="https://emerv.com" target="_blank" rel="noopener noreferrer">EmerV</a>🌸 @ <a href="https://infinia.ie" target="_blank" rel="noopener noreferrer">Infinia</a>
              </PoweredBy>
            </InputContainer>
          </ChatWindow>
        )}
      </AnimatePresence>

      <MobileHiddenToggle
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.3 }}
        $isOpen={isOpen}
      >
        {isOpen ? <X size={28} /> : <Moon size={28} />}
      </MobileHiddenToggle>
    </ChatbotWrapper>
  );
};

export default Luna; 
